import React, { memo, useCallback } from "react";
import { ReportData } from "../../types/global.d";
import { FilterType } from "./reportsUtils";
import SidebarErrorState from "./SidebarErrorState";
import SidebarLoadingState from "./SidebarLoadingState";
import ReportsList from "./ReportsList";
import { useSidebarCollapse } from "./useSidebarHooks";
import "../styles/ReportsSidebar.css";

interface ReportsSidebarProps {
  reports: ReportData[];
  loading?: boolean;
  error?: string | null;
  onReportClick?: (report: ReportData) => void;
  selectedReportId?: string | null;
  onFilterChange?: (filter: FilterType) => void;
  userFilter?: string | null;
  onUserFilterChange?: (nickname: string | null) => void;
}

const ReportsSidebar: React.FC<ReportsSidebarProps> = memo(
  ({
    reports,
    loading = false,
    error = null,
    onReportClick,
    selectedReportId,
    onFilterChange,
    userFilter,
    onUserFilterChange,
  }) => {
    const { isCollapsed, toggleCollapse } = useSidebarCollapse();

    const handleReportClick = useCallback(
      (report: ReportData) => {
        if (onReportClick) {
          onReportClick(report);
        }
      },
      [onReportClick]
    );

    const renderContent = () => {
      if (error) {
        return <SidebarErrorState error={error} />;
      }

      if (loading) {
        return <SidebarLoadingState />;
      }

      return (
        <ReportsList
          reports={reports}
          onReportClick={handleReportClick}
          selectedReportId={selectedReportId}
          onClose={toggleCollapse}
          onFilterChange={onFilterChange}
          userFilter={userFilter}
          onUserFilterChange={onUserFilterChange}
        />
      );
    };

    return (
      <div className={`reports-sidebar ${isCollapsed ? "collapsed" : ""}`}>
        {isCollapsed ? (
          <button
            className="circular-menu-button"
            onClick={toggleCollapse}
            aria-label="Open sidebar"
          >
            <span className="menu-icon">☰</span>
          </button>
        ) : (
          <div className="sidebar-content">{renderContent()}</div>
        )}
      </div>
    );
  }
);

ReportsSidebar.displayName = "ReportsSidebar";

export default ReportsSidebar;
